import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  cardClasses,
  cn,
  Field,
  Modal,
  ModalActions,
  Page,
  PageHeader,
  StepTrail,
  type TrailStep,
} from '@evogard/ui';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@evogard/ui/primitives';
import type { AsaasOverview, IntegrationCredential, TestResult } from '@evogard/contract';
import {
  ASAAS_ENVIRONMENT_LABELS,
  canAccess,
  credentialProblem,
  SGA_TOKEN_LENGTH,
  sgaAssociationTokenProblem,
} from '@evogard/domain';
import { api, ok, errorMessage } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import { ContaazulAccountsCard, useContaazulAccounts } from './ContaazulAccountsCard.js';
import {
  OpasuiteAddressPanel,
  OpasuitePurposesPanel,
  OpasuiteTemplatesPanel,
  useOpasuiteOverview,
} from './OpasuitePanels.js';
import {
  AsaasEnvironmentSection,
  AsaasWebhookPanel,
  useAsaasOverview,
} from './AsaasPanels.js';
import {
  INTEGRATION_GROUPS,
  INTEGRATION_PURPOSES,
  integrationState,
  type IntegrationState,
} from './integrationState.js';
import { CafTemplatesPanel, CafWebhookPanel } from './CafPanels.js';
import { SendgridSenderPanel, useSendgridSenderMissing } from './SendgridPanels.js';

/**
 * As integrações da base — a regra 12.
 *
 * **Lista e detalhe, desde 16/10/2026.** A tela empilhava sete cartões numa
 * coluna, e o Asaas e o OpaSuite eram dois cartões cada, soltos um do outro;
 * para saber o que estava quebrado era preciso rolar a página inteira. Agora a
 * lista à esquerda diz a situação de cada integração (`integrationState`), e
 * o detalhe à direita mostra uma só, com abas quando há mais de um assunto. A
 * integração aberta vai no endereço (`?integration=gateway`), para um aviso
 * poder apontar para ela.
 *
 * A tela nunca recebe a credencial: só a prévia de cinco caracteres, cortada
 * no servidor (`previewOf`). Colar, gerar e remover são modais com
 * confirmação, e a forma de cada credencial é conferida pela regra do domínio
 * (`credential-format.ts`) — o servidor recusa com a mesma regra.
 *
 * A geração pede os três — token da associação, usuário e senha do SGA — e
 * guarda só o token de sessão que o SGA devolve. Desde 14/10/2026 não há
 * padrão no `.env`: cada base tem a sua, e nenhum login é salvo no banco.
 */
export function IntegrationsScreen() {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const tenantName = context?.currentTenant.nome ?? 'esta base';
  const search = useSearch({ from: '/authenticated/integrations' });
  const navigate = useNavigate();

  const canEdit = canAccess(
    context?.permissions ?? [],
    'platform.integrations',
    'editar',
  );
  const canTest = canAccess(
    context?.permissions ?? [],
    'platform.integrations',
    'testar',
  );

  const query = useQuery({
    // a base na chave: trocar de praça troca as credenciais que estão na tela
    queryKey: ['plataforma', 'integracoes', tenantId],
    queryFn: async () => {
      const r = await api.platform.listIntegrations();
      if (r.status !== 200) {
        throw new Error(errorMessage(r.body, 'Não foi possível carregar.'));
      }
      return r.body.integrations;
    },
    enabled: tenantId !== undefined,
  });

  /*
   * As três consultas que a lista precisa para dizer o que falta — as mesmas
   * que os painéis leem, então abrir a integração não busca de novo.
   */
  const asaas = useAsaasOverview(tenantId);
  const opasuite = useOpasuiteOverview(tenantId);
  const contaazul = useContaazulAccounts(tenantId);
  const sendgridSenderMissing = useSendgridSenderMissing(tenantId);

  const integrations = query.data ?? [];
  const states = new Map<string, IntegrationState | null>(
    integrations.map((i) => [
      i.integration,
      i.integration === 'contaazul'
        ? contaazulState(contaazul.data?.length)
        : integrationState(
            i,
            i.integration === 'gateway' && asaas.data && !asaas.data.webhook.registered
              ? 'Falta o webhook'
              : i.integration === 'opasuite' &&
                  opasuite.data &&
                  !opasuite.data.settings.baseUrl
                ? 'Falta o endereço'
                : i.integration === 'sendgrid' && sendgridSenderMissing === true
                  ? 'Falta o remetente'
                  : null,
          ),
    ]),
  );

  const selected =
    integrations.find((i) => i.integration === search.integration) ?? integrations[0];
  const select = (integration: string) =>
    void navigate({ to: '/integrations', search: { integration }, replace: true });

  return (
    <Page measure="cheia">
      <PageHeader
        title="Integrações"
        actions={<Summary states={[...states.values()].filter((st) => st !== null)} />}
      >
        Base{' '}
        <strong className="font-medium text-texto">{context?.currentTenant.nome}</strong>.
        Cada base tem as suas credenciais.
      </PageHeader>

      {query.isError && (
        <Alert tone="erro" title="Não foi possível carregar as integrações.">
          {query.error.message}
        </Alert>
      )}

      {selected && (
        <div className="grid items-start gap-4 lg:grid-cols-[17rem_minmax(0,1fr)]">
          <IntegrationList
            integrations={integrations}
            states={states}
            selected={selected.integration}
            onSelect={select}
          />
          {/* a chave reabre o detalhe na primeira aba, e limpa os avisos da anterior */}
          <IntegrationDetail
            key={`${selected.integration}-${tenantId}`}
            integration={selected}
            state={states.get(selected.integration) ?? null}
            asaas={asaas.data ?? null}
            tenantId={tenantId}
            tenantName={tenantName}
            canEdit={canEdit}
            canTest={canTest}
          />
        </div>
      )}
    </Page>
  );
}

/** O Conta Azul não tem credencial única: a situação dele é quantas contas a base tem. */
function contaazulState(accounts: number | undefined): IntegrationState | null {
  if (accounts === undefined) return null;
  if (accounts === 0) return { tone: 'neutro', label: 'Nenhuma conta' };
  return { tone: 'neutro', label: accounts === 1 ? '1 conta' : `${accounts} contas` };
}

/** O resumo no alto: quantas têm problema, quantas faltam completar, quantas responderam. */
function Summary({ states }: { states: IntegrationState[] }) {
  const count = (tone: IntegrationState['tone']) =>
    states.filter((s) => s.tone === tone).length;
  const failing = count('erro');
  const incomplete = count('alerta');
  const working = count('sucesso');
  if (failing + incomplete + working === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {failing > 0 && <Badge tone="erro">{failing} com problema</Badge>}
      {incomplete > 0 && (
        <Badge tone="alerta">
          {incomplete} {incomplete === 1 ? 'incompleta' : 'incompletas'}
        </Badge>
      )}
      {working > 0 && <Badge tone="sucesso">{working} com conexão OK</Badge>}
    </div>
  );
}

/**
 * A lista, agrupada pelo que cada integração faz. No telefone ela vira uma
 * faixa que rola de lado acima do detalhe, e os nomes dos grupos somem.
 */
function IntegrationList({
  integrations,
  states,
  selected,
  onSelect,
}: {
  integrations: IntegrationCredential[];
  states: Map<string, IntegrationState | null>;
  selected: string;
  onSelect: (integration: string) => void;
}) {
  const grouped = new Set(INTEGRATION_GROUPS.flatMap((g) => g.integrations));
  const groups = [
    ...INTEGRATION_GROUPS.map((g) => ({
      label: g.label,
      items: g.integrations
        .map((key) => integrations.find((i) => i.integration === key))
        .filter((i): i is IntegrationCredential => i !== undefined),
    })),
    { label: 'Outras', items: integrations.filter((i) => !grouped.has(i.integration)) },
  ].filter((g) => g.items.length > 0);

  return (
    <nav
      aria-label="Integrações"
      className={cardClasses({
        padding: false,
        className: 'rolagem-lateral flex gap-1 p-2 lg:flex-col lg:overflow-visible',
      })}
    >
      {groups.map((group) => (
        <div key={group.label} className="flex gap-1 lg:flex-col">
          <p className="rotulo hidden px-2.5 pt-3 pb-1 text-texto-tenue lg:block">
            {group.label}
          </p>
          {group.items.map((i) => {
            const state = states.get(i.integration);
            const current = i.integration === selected;
            return (
              <button
                key={i.integration}
                type="button"
                onClick={() => onSelect(i.integration)}
                aria-current={current ? 'page' : undefined}
                className={cn(
                  'flex min-w-48 shrink-0 flex-col gap-1 rounded-borda px-2.5 py-2 text-left lg:min-w-0',
                  'transition-colors pointer-coarse:min-h-11',
                  current ? 'bg-primaria-suave' : 'hover:bg-superficie-suave',
                )}
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-semibold text-texto">
                    {i.label}
                  </span>
                  {state && (
                    <span className="shrink-0 whitespace-nowrap">
                      <Badge tone={state.tone}>{state.label}</Badge>
                    </span>
                  )}
                </span>
                {INTEGRATION_PURPOSES[i.integration] && (
                  <span className="truncate text-xs text-texto-suave">
                    {INTEGRATION_PURPOSES[i.integration]}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

interface DetailProps {
  integration: IntegrationCredential;
  state: IntegrationState | null;
  asaas: AsaasOverview | null;
  tenantId: number | undefined;
  tenantName: string;
  canEdit: boolean;
  canTest: boolean;
}

/** Um assunto do detalhe: com dois ou mais, viram abas. */
interface Topic {
  key: string;
  label: string;
  /** O ponto de alerta na aba — o assunto que ainda falta. */
  pending?: boolean;
  content: ReactNode;
}

/**
 * O detalhe da integração aberta. O Conta Azul usa o cartão dele, que já é uma
 * lista de contas; as outras são um cartão com a credencial e, onde a
 * integração pede mais, abas.
 */
function IntegrationDetail(props: DetailProps) {
  const { integration, state, asaas, tenantId, canEdit } = props;
  const [tab, setTab] = useState<string | null>(null);

  if (integration.integration === 'contaazul') {
    return (
      <ContaazulAccountsCard
        label={integration.label}
        tenantId={tenantId}
        canEdit={canEdit}
        canTest={props.canTest}
      />
    );
  }

  const credential = <CredentialSection {...props} />;
  const topics: Topic[] =
    integration.integration === 'gateway'
      ? [
          {
            key: 'conexao',
            label: 'Conexão',
            content: (
              <>
                <AsaasEnvironmentSection tenantId={tenantId} canEdit={canEdit} />
                {credential}
              </>
            ),
          },
          {
            key: 'webhook',
            label: 'Webhook',
            pending: asaas !== null && !asaas.webhook.registered,
            content: <AsaasWebhookPanel tenantId={tenantId} canEdit={canEdit} />,
          },
        ]
      : integration.integration === 'opasuite'
        ? [
            {
              key: 'conexao',
              label: 'Conexão',
              content: (
                <>
                  {credential}
                  <OpasuiteAddressPanel tenantId={tenantId} canEdit={canEdit} />
                </>
              ),
            },
            {
              key: 'templates',
              label: 'Templates',
              content: <OpasuiteTemplatesPanel tenantId={tenantId} canEdit={canEdit} />,
            },
            {
              key: 'pontos',
              label: 'Pontos de envio',
              content: <OpasuitePurposesPanel tenantId={tenantId} canEdit={canEdit} />,
            },
          ]
        : integration.integration === 'caf'
          ? [
              { key: 'conexao', label: 'Conexão', content: credential },
              {
                key: 'templates',
                label: 'Templates',
                content: <CafTemplatesPanel tenantId={tenantId} />,
              },
              {
                key: 'webhook',
                label: 'Webhook',
                content: <CafWebhookPanel tenantId={tenantId} />,
              },
            ]
          : integration.integration === 'sendgrid'
            ? [
                { key: 'conexao', label: 'Conexão', content: credential },
                {
                  key: 'remetente',
                  label: 'Remetente',
                  pending: state?.label === 'Falta o remetente',
                  content: <SendgridSenderPanel tenantId={tenantId} />,
                },
              ]
            : [{ key: 'conexao', label: 'Conexão', content: credential }];

  /* os blocos de um assunto, separados por um fio: credencial, ambiente, endereço */
  const body = (content: ReactNode) => (
    <div className="flex flex-col gap-6 p-4 [&>*+*]:border-t [&>*+*]:border-borda-suave [&>*+*]:pt-6">
      {content}
    </div>
  );

  return (
    <Card
      bleed
      title={integration.label}
      descricao={INTEGRATION_PURPOSES[integration.integration]}
      actions={state && <Badge tone={state.tone}>{state.label}</Badge>}
    >
      {integration.integration === 'gateway' && (
        <AsaasSteps integration={integration} asaas={asaas} />
      )}
      {topics.length === 1 ? (
        body(topics[0]!.content)
      ) : (
        <Tabs value={tab ?? topics[0]!.key} onValueChange={setTab}>
          <TabsList aria-label={`Assuntos de ${integration.label}`}>
            {topics.map((t) => (
              <TabsTrigger key={t.key} value={t.key}>
                <span className="flex items-center gap-1.5">
                  {t.label}
                  {t.pending && (
                    <span
                      role="img"
                      aria-label="falta configurar"
                      className="size-1.5 rounded-full bg-alerta"
                    />
                  )}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>
          {topics.map((t) => (
            <TabsContent key={t.key} value={t.key}>
              {body(t.content)}
            </TabsContent>
          ))}
        </Tabs>
      )}
    </Card>
  );
}

/**
 * Os três passos do Asaas, na ordem em que valem: a chave é do ambiente, e o
 * webhook é da conta da chave. Trocar o ambiente apaga o webhook registrado.
 */
function AsaasSteps({
  integration,
  asaas,
}: {
  integration: IntegrationCredential;
  asaas: AsaasOverview | null;
}) {
  if (!asaas) return null;
  const hasKey = integration.configured && !integration.unreadable;
  const steps: TrailStep[] = [
    {
      key: 'ambiente',
      label: `Ambiente: ${ASAAS_ENVIRONMENT_LABELS[asaas.environment]}`,
      state: 'done',
    },
    { key: 'chave', label: 'Chave de API', state: hasKey ? 'done' : 'now' },
    {
      key: 'webhook',
      label: 'Webhook',
      state: asaas.webhook.registered ? 'done' : hasKey ? 'now' : 'next',
    },
  ];
  return <StepTrail label="Configuração do Asaas" steps={steps} />;
}

type Dialog = 'colar' | 'gerar' | 'remover' | null;

/** A credencial da integração: a prévia, o último teste e os atos sobre ela. */
function CredentialSection({
  integration,
  tenantId,
  tenantName,
  canEdit,
  canTest,
}: DetailProps) {
  const client = useQueryClient();
  const [dialog, setDialog] = useState<Dialog>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  const key = ['plataforma', 'integracoes', tenantId];

  /** O que todo modal faz ao terminar bem: lista nova, aviso na página, modal fechado. */
  const done = (list: IntegrationCredential[], message: string) => {
    client.setQueryData(key, list);
    setError(null);
    setTestResult(null);
    setNote(message);
    setDialog(null);
  };

  const test = useMutation({
    mutationFn: async () => {
      const r = await api.platform.testIntegration({
        params: { integration: integration.integration },
        body: {},
      });
      return ok(r, 200, 'Não foi possível testar.');
    },
    onSuccess: (body) => {
      setError(null);
      setNote(null);
      setTestResult(body.result);
      client.setQueryData(key, body.integrations);
    },
    onError: (e: Error) => setError(e.message),
  });

  const hasOwn = integration.configured;
  const canRunTest = canTest && integration.hasPort && integration.configured;

  return (
    <section
      className="flex flex-col gap-3"
      aria-labelledby={`credencial-${integration.integration}`}
    >
      <h3
        id={`credencial-${integration.integration}`}
        className="text-sm font-semibold text-texto"
      >
        {integration.integration === 'gateway' ? 'Chave de API' : 'Credencial'}
      </h3>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <StateBadge integration={integration} />
          {integration.verifiedAt && integration.verifiedOk !== null && (
            <Badge tone={integration.verifiedOk ? 'sucesso' : 'erro'}>
              {integration.verifiedOk ? 'Conexão OK' : 'Conexão falhou'} em{' '}
              {formatDate(integration.verifiedAt)}
            </Badge>
          )}
        </div>

        {integration.secretPreview && (
          <dl className="grid gap-1 text-sm sm:grid-cols-[auto_1fr] sm:gap-x-4">
            <dt className="text-texto-suave">Token</dt>
            <dd>
              <Preview value={integration.secretPreview} />
            </dd>
          </dl>
        )}

        {integration.unreadable && (
          <Alert tone="erro" title="A credencial não abre.">
            A chave de cifra da aplicação mudou. Aplique a credencial de novo.
          </Alert>
        )}

        {error && <Alert tone="erro">{error}</Alert>}
        {note && <Alert tone="informacao">{note}</Alert>}
        {testResult && (
          <Alert tone={testResult.ok ? 'sucesso' : 'erro'}>{testResult.detail}</Alert>
        )}

        {(canEdit || canRunTest) && (
          /*
           * A ordem é a do uso (09/10/2026): a ação principal primeiro e em
           * destaque — gerar, no Hinova, que é como a credencial nasce; colar,
           * onde não há geração —, depois as secundárias, e o remover sozinho
           * à direita, longe de onde o clique costuma cair.
           */
          <div className="flex flex-wrap items-center gap-2">
            {canEdit && integration.canGenerate && (
              <Button
                icon="atualizar"
                onClick={() => {
                  setError(null);
                  setDialog('gerar');
                }}
              >
                {hasOwn ? 'Gerar nova credencial' : 'Gerar credencial'}
              </Button>
            )}
            {canEdit && (
              <Button
                variant={integration.canGenerate ? 'contorno' : 'primario'}
                onClick={() => {
                  setError(null);
                  setDialog('colar');
                }}
              >
                {integration.canGenerate
                  ? 'Inserir token manualmente'
                  : hasOwn
                    ? 'Substituir credencial'
                    : 'Colar credencial'}
              </Button>
            )}
            {canRunTest && (
              <Button
                variant="contorno"
                disabled={test.isPending}
                onClick={() => test.mutate()}
              >
                {test.isPending ? 'Testando…' : 'Testar conexão'}
              </Button>
            )}
            {canEdit && hasOwn && (
              <Button
                variant="perigo"
                className="sm:ml-auto"
                onClick={() => {
                  setError(null);
                  setDialog('remover');
                }}
              >
                Remover
              </Button>
            )}
          </div>
        )}

        {!integration.hasPort && (
          <p className="text-sm text-texto-suave">
            Sem conexão com o fornecedor ainda: dá para guardar a credencial, não para
            testar.
          </p>
        )}
      </div>

      {dialog === 'colar' && (
        <PasteCredentialModal
          integration={integration}
          tenantName={tenantName}
          onClose={() => setDialog(null)}
          onDone={done}
        />
      )}
      {dialog === 'gerar' && (
        <GenerateCredentialModal
          integration={integration}
          tenantName={tenantName}
          onClose={() => setDialog(null)}
          onDone={done}
        />
      )}
      {dialog === 'remover' && (
        <RemoveCredentialModal
          integration={integration}
          tenantName={tenantName}
          onClose={() => setDialog(null)}
          onDone={done}
        />
      )}
    </section>
  );
}

interface DialogProps {
  integration: IntegrationCredential;
  tenantName: string;
  onClose: () => void;
  onDone: (list: IntegrationCredential[], message: string) => void;
}

/** Uma prévia mascarada, em fonte de código: `7b2e1••••••••••••`. */
function Preview({ value }: { value: string }) {
  return <code className="font-mono tabular-nums text-texto">{value}</code>;
}

/** A frase da confirmação sobre o que já está lá: aplicar a primeira, ou trocar a atual. */
function Replacing({ integration, tenantName }: Omit<DialogProps, 'onClose' | 'onDone'>) {
  if (!integration.configured) {
    return (
      <p className="text-sm text-texto-suave">
        Será aplicada à base{' '}
        <strong className="font-medium text-texto">{tenantName}</strong>.
      </p>
    );
  }
  return (
    <p className="text-sm text-texto-suave">
      Substitui a credencial atual da base{' '}
      <strong className="font-medium text-texto">{tenantName}</strong>
      {integration.secretPreview && (
        <>
          {' '}
          (<Preview value={integration.secretPreview} />)
        </>
      )}
      . Não dá para desfazer.
    </p>
  );
}

/** A regra de forma desta integração, no campo. Ela é do domínio, e o servidor recusa igual. */
function formatHelp(integration: IntegrationCredential): ReactNode {
  if (integration.integration === 'hinova') {
    return `${SGA_TOKEN_LENGTH} caracteres, de 0–9 e a–f.`;
  }
  if (integration.integration === 'sendgrid') {
    return 'Começa com "SG.". Gere em Settings › API Keys, com a permissão Mail Send.';
  }
  return 'Sem espaços.';
}

function PasteCredentialModal({ integration, tenantName, onClose, onDone }: DialogProps) {
  const [secret, setSecret] = useState('');
  const [touched, setTouched] = useState(false);
  const [step, setStep] = useState<'preencher' | 'confirmar'>('preencher');
  const [failure, setFailure] = useState<string | null>(null);

  const problem = credentialProblem(integration.integration, secret);
  const replacing = integration.configured;

  const apply = useMutation({
    mutationFn: async () => {
      const r = await api.platform.applyCredential({
        params: { integration: integration.integration },
        body: { secret: secret.trim() },
      });
      return ok(r, 200, 'Não foi possível aplicar.');
    },
    onSuccess: (body) => {
      // o campo morre com o modal: o token não fica na tela depois de gravado
      setSecret('');
      onDone(
        body.integrations,
        body.switched ? 'Credencial aplicada.' : 'Essa credencial já estava aplicada.',
      );
    },
    onError: (e: Error) => {
      setFailure(e.message);
      setStep('preencher');
    },
  });

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title={`${replacing ? 'Substituir' : 'Colar'} credencial — ${integration.label}`}
      size="md"
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (step === 'preencher') {
            setTouched(true);
            if (problem === null) {
              setFailure(null);
              setStep('confirmar');
            }
            return;
          }
          apply.mutate();
        }}
      >
        {failure && <Alert tone="erro">{failure}</Alert>}

        {step === 'preencher' ? (
          <Field
            label="Credencial"
            /*
             * `password` e `off`: o navegador guardaria o token da CE e o
             * ofereceria no formulário da GO.
             */
            type="password"
            autoComplete="off"
            spellCheck={false}
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            onBlur={() => setTouched(true)}
            error={touched ? problem : null}
            help={formatHelp(integration)}
          />
        ) : (
          <Replacing integration={integration} tenantName={tenantName} />
        )}

        <ModalActions>
          {step === 'preencher' ? (
            <>
              <Button type="button" variant="contorno" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="submit" disabled={secret.trim().length === 0}>
                Continuar
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="contorno"
                disabled={apply.isPending}
                onClick={() => setStep('preencher')}
              >
                Voltar
              </Button>
              <Button type="submit" disabled={apply.isPending}>
                {apply.isPending ? 'Aplicando…' : replacing ? 'Substituir' : 'Aplicar'}
              </Button>
            </>
          )}
        </ModalActions>
      </form>
    </Modal>
  );
}

function GenerateCredentialModal({
  integration,
  tenantName,
  onClose,
  onDone,
}: DialogProps) {
  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [associationToken, setAssociationToken] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [step, setStep] = useState<'preencher' | 'confirmar'>('preencher');
  const [failure, setFailure] = useState<string | null>(null);

  const problems = {
    user: user.trim() ? null : 'Informe o usuário.',
    password: password ? null : 'Informe a senha.',
    associationToken: sgaAssociationTokenProblem(associationToken),
  };
  const hasProblem = Object.values(problems).some((p) => p !== null);
  const replacing = integration.configured;

  // a senha e o token saem do formulário em qualquer desfecho; o usuário fica
  const forgetSecrets = () => {
    setPassword('');
    setAssociationToken('');
  };

  const generate = useMutation({
    mutationFn: async () => {
      const r = await api.platform.generateCredential({
        params: { integration: integration.integration },
        body: {
          user: user.trim(),
          password,
          associationToken: associationToken.trim(),
        },
      });
      return ok(r, 200, 'Não foi possível gerar.');
    },
    onSuccess: (body) => {
      forgetSecrets();
      if (body.generated) {
        onDone(body.integrations, body.detail);
        return;
      }
      setFailure(body.detail);
      setSubmitted(false);
      setStep('preencher');
    },
    onError: (e: Error) => {
      forgetSecrets();
      setFailure(e.message);
      setSubmitted(false);
      setStep('preencher');
    },
  });

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title="Gerar credencial a partir de um usuário do SGA"
      size="md"
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (step === 'preencher') {
            setSubmitted(true);
            if (!hasProblem) {
              setFailure(null);
              setStep('confirmar');
            }
            return;
          }
          generate.mutate();
        }}
      >
        {failure && <Alert tone="erro">{failure}</Alert>}

        {step === 'preencher' ? (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label="Usuário do SGA"
                autoComplete="off"
                spellCheck={false}
                value={user}
                onChange={(e) => setUser(e.target.value)}
                error={submitted ? problems.user : null}
              />
              <Field
                label="Senha"
                type="password"
                autoComplete="off"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={submitted ? problems.password : null}
              />
            </div>
            <Field
              label="Token da associação"
              type="password"
              autoComplete="off"
              spellCheck={false}
              value={associationToken}
              onChange={(e) => setAssociationToken(e.target.value)}
              error={submitted ? problems.associationToken : null}
              help="O da Área Cliente do SGA, desta praça. Ele é conferido no login."
            />
          </>
        ) : (
          <div className="flex flex-col gap-2">
            <Replacing integration={integration} tenantName={tenantName} />
            <p className="text-sm text-texto-suave">
              O que você digitou não fica guardado.
            </p>
          </div>
        )}

        <ModalActions>
          {step === 'preencher' ? (
            <>
              <Button type="button" variant="contorno" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="submit">Continuar</Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="contorno"
                disabled={generate.isPending}
                onClick={() => setStep('preencher')}
              >
                Voltar
              </Button>
              <Button type="submit" disabled={generate.isPending}>
                {generate.isPending
                  ? 'Gerando…'
                  : replacing
                    ? 'Gerar e substituir'
                    : 'Gerar'}
              </Button>
            </>
          )}
        </ModalActions>
      </form>
    </Modal>
  );
}

function RemoveCredentialModal({
  integration,
  tenantName,
  onClose,
  onDone,
}: DialogProps) {
  const [failure, setFailure] = useState<string | null>(null);

  const discard = useMutation({
    mutationFn: async () => {
      const r = await api.platform.discardCredential({
        params: { integration: integration.integration },
      });
      return ok(r, 200, 'Não foi possível remover.');
    },
    onSuccess: (body) => onDone(body.integrations, 'Credencial removida.'),
    onError: (e: Error) => setFailure(e.message),
  });

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title={`Remover credencial — ${integration.label}`}
      size="md"
    >
      <div className="flex flex-col gap-4">
        {failure && <Alert tone="erro">{failure}</Alert>}
        <p className="text-sm text-texto-suave">
          A base <strong className="font-medium text-texto">{tenantName}</strong> fica sem
          credencial do {integration.label}. Não dá para desfazer.
        </p>
        <ModalActions>
          <Button
            type="button"
            variant="contorno"
            disabled={discard.isPending}
            onClick={onClose}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="perigo"
            disabled={discard.isPending}
            onClick={() => {
              setFailure(null);
              discard.mutate();
            }}
          >
            {discard.isPending ? 'Removendo…' : 'Remover'}
          </Button>
        </ModalActions>
      </div>
    </Modal>
  );
}

function StateBadge({ integration }: { integration: IntegrationCredential }) {
  if (integration.configured) {
    return (
      <Badge tone="sucesso">
        Configurada
        {integration.updatedAt && ` · ${formatDate(integration.updatedAt)}`}
      </Badge>
    );
  }
  return <Badge tone="neutro">Sem credencial</Badge>;
}

/** Data e hora curtas. `Intl` porque `toLocaleString` sem opção varia por máquina. */
function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(iso));
}
